import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Phone, FileText, TestTube, Syringe, ClipboardList, Download, AlertTriangle } from 'lucide-react';
import HospitalIcon from '../shared/icons/HospitalIcon';
import ToolHeader from '../shared/ToolHeader';
import { Section, Card, IconTile } from '../hospital/shared/primitives';

/*
 * Needlestick and exposure: the steps that apply everywhere, then each hospital's own
 * reporting protocol. Only BSLMC / THI has a written protocol so far; the others say so
 * plainly and point to occupational health.
 */

const ACCENT = '#be123c';

const FIRST_STEPS = [
  'Wash the wound with soap and water.',
  'Flush splashed eyes, nose or mouth with water or saline.',
  'Notify the supervising attending or charge nurse.',
  'Complete the hospital incident report the same day.',
  'Follow the protocol for the hospital below.',
];

const HOSPITALS = [
  { id: 'st-lukes', secondaryId: 'texas-heart', name: "Baylor St. Luke's / Texas Heart Institute", protocol: 'bslmc' },
  { id: 'ben-taub', name: 'Ben Taub General Hospital' },
  { id: 'texas-childrens', name: "Texas Children's Hospital" },
  { id: 'va', name: 'Michael E. DeBakey VA Medical Center' },
  { id: 'md-anderson', name: 'MD Anderson Cancer Center' },
];

const Block = ({ icon, title, children }) => (
  <div className="px-4 py-3">
    <div className="flex items-center gap-2 mb-2">
      <IconTile icon={icon} accent={ACCENT} />
      <h4 className="font-semibold text-[15px] text-gray-900 dark:text-white">{title}</h4>
    </div>
    <div className="text-sm text-gray-700 dark:text-gray-300 leading-snug space-y-2">{children}</div>
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-1.5">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2.5">
        <span className="w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${ACCENT}1f`, color: ACCENT }}>{i + 1}</span>
        <span>{t}</span>
      </li>
    ))}
  </ol>
);

const Bullets = ({ items }) => (
  <ul className="space-y-1 pl-4 list-disc marker:text-gray-400">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

const Callout = ({ children }) => (
  <div className="rounded-xl px-3 py-2.5 text-sm" style={{ backgroundColor: `${ACCENT}0f`, color: '#881337' }}>{children}</div>
);

const BslmcProtocol = () => (
  <>
    <Block icon={FileText} title="Report the injury">
      <p>Notify the manager or supervisor, then file an IRIS Reporter entry on The Source under Quick Links.</p>
      <Steps items={[
        'Open OneNet Texas and select IRIS Reporter under Quick Tools.',
        'Select "Click here to begin", then "Employee" at the bottom right.',
        'Complete the report and select "Submit Incident" at the top right.',
      ]} />
    </Block>
    <Block icon={TestTube} title="Source patient blood">
      <p>Collect one red-top tube from the source patient.</p>
      <Callout>
        Label the tube <strong>SP</strong>, the exposed person's initials, and the last four digits of their SSN, for example <span className="font-mono">SP-js1234</span>. Do not place the patient's hospital label on the tube or bag.
      </Callout>
    </Block>
    <Block icon={TestTube} title="Exposed person's blood">
      <Bullets items={[
        'Source known positive or high risk for HIV or hepatitis C: collect three red-top tubes from the exposed person.',
        'Source status unknown: send the source specimen for testing; no draw from the exposed person yet.',
        'Source patient unknown: see the collection table in the full protocol.',
      ]} />
      <p className="text-xs text-gray-500 dark:text-gray-400">Off site, call the courier for pickup and delivery to the St. Luke's laboratory: (832) 355-3053.</p>
    </Block>
    <Block icon={Phone} title="Who to contact">
      <p><span className="font-medium">Business hours</span> (Mon to Thu 6 am to 4 pm, Fri 6 am to 12 pm): Occupational Health.</p>
      <a href="tel:832-355-6040" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}><Phone size={15} /> (832) 355-6040</a>
      <p><span className="font-medium">After hours and weekends:</span> ask the operator to page the House Supervisor. If unavailable, go to the Emergency Room charge nurse.</p>
      <p><span className="font-medium">To escalate:</span> contact the Chief Medical Officer, Dr. Carolyn Quan, through TigerConnect.</p>
    </Block>
    <Block icon={AlertTriangle} title="Source known positive for HIV, hepatitis B or hepatitis C">
      <p><span className="font-medium">From the source patient:</span> one red-top tube.</p>
      <p><span className="font-medium">From the exposed person</span> (complete Form 3): three red-top tubes, one purple-top tube, one light-green-top tube, and a urine specimen. Label each with name, date of birth, sex, date and time drawn, and the collector's initials.</p>
      <p>If the exposed person consents to HIV post-exposure prophylaxis, order it on the Post Exposure Consent to Testing form.</p>
    </Block>
    <Block icon={ClipboardList} title="Forms">
      <p><span className="font-medium">Form 1, source patient:</span> signed and dated by the Occupational Health nurse, House Supervisor or ER charge nurse. Fax a copy to (713) 610-2596 and send the original with the source blood to the laboratory in a biohazard bag.</p>
      <p><span className="font-medium">Form 2, exposed person:</span> signed and dated by the same; fax to (713) 610-2596. Occupational Health contacts the exposed person with source results and any need for a draw.</p>
      <p><span className="font-medium">Provide the exposed person</span> with the "Exposure to Blood, Healthcare Personnel" booklet from the Occupational Health protocols page and fax the signed acknowledgement.</p>
      <p><span className="font-medium">By the next day, fax to Occupational Health:</span> Forms 1 to 3, the CDC acknowledgement, the post-exposure procedures, and the consent to testing.</p>
      <p>The exposed person contacts Occupational Health at (832) 355-6040 by the next business day.</p>
    </Block>
    <div className="px-4 py-3">
      <a href={`${process.env.PUBLIC_URL}/bslmc/needlestick-protocol.pdf`} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100">
        <Download size={16} /> Full protocol with forms (PDF)
      </a>
    </div>
  </>
);

const NoProtocol = ({ name }) => (
  <div className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 leading-snug">
    No written protocol for {name} is in the app yet. Report to the supervising attending and the hospital's occupational health service, and complete the hospital's incident report.
  </div>
);

const NeedlestickScreen = ({ onBack }) => {
  const [open, setOpen] = useState(null);

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToolHeader title="Needlestick Injury" subtitle="Exposure protocols by hospital" icon={Syringe} accent={ACCENT} backLabel="Home" onBack={onBack} />
      <div className="p-6 space-y-6">
        <Section title="First, at every site">
          <Card>
            <div className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300"><Steps items={FIRST_STEPS} /></div>
          </Card>
        </Section>

        <Section title="Your hospital">
          <div className="space-y-3">
            {HOSPITALS.map((h) => {
              const isOpen = open === h.id;
              return (
                <Card key={h.id}>
                  <button type="button" onClick={() => setOpen(isOpen ? null : h.id)} aria-expanded={isOpen} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <div className="w-9 h-9 bg-white rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center"><HospitalIcon type={h.id} className="w-6 h-6" /></div>
                      {h.secondaryId && <div className="w-9 h-9 bg-white rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center"><HospitalIcon type={h.secondaryId} className="w-6 h-6" /></div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[15px] text-gray-900 dark:text-white leading-tight">{h.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: h.protocol ? ACCENT : undefined }}>{h.protocol ? <span className="font-medium">Protocol available</span> : <span className="text-gray-400 dark:text-gray-500">Protocol not yet available</span>}</div>
                    </div>
                    {isOpen ? <ChevronDown size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" /> : <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
                  </button>
                  {isOpen && (h.protocol === 'bslmc' ? <BslmcProtocol /> : <NoProtocol name={h.name} />)}
                </Card>
              );
            })}
          </div>
        </Section>
      </div>
      <div className="h-8" />
    </div>
  );
};

export default NeedlestickScreen;
